/**
 * iter 144q - Start Quiz (/start) - paid-social entry funnel.
 *
 * Self-contained 14-screen flow. No nav, no header, no footer, no exit
 * points. Captures email/first-name mid-flow → MongoDB (`role:
 * "quiz_lead"`, `source: "start_quiz"`). UTM params + LAUNCH25 promo
 * code captured via the shared `lib/utm` + `lib/promo` modules that run
 * on every route change in `App.js`. On purchase CTA the user is
 * forwarded to the existing Blueprint / Community flows with
 * `?promo=LAUNCH25&tier=<key>` so the discount and tier card are
 * pre-applied.
 *
 * Design rules baked in:
 *   • No question numbers, no "X of 8" copy. The amber bar at the top
 *     is the ONLY progress indicator.
 *   • Auto-advance 300ms after selecting any option on Q1–Q8.
 *   • Buffer screens auto-advance after 5s OR on tap-anywhere.
 *   • Loading screen progresses over exactly 8s with three sub-lines
 *     revealed every 2s.
 *   • Result-screen headline is computed from the answer signals.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BRAND_LOGO } from '../constants/brand';
import { PRICING_CONTENT } from '../constants/pricingContent';
import { getStoredPromo } from '../lib/promo';
import {
  trackQuizStarted,
  trackQuizEmailCaptured,
  trackQuizCompleted,
  trackQuizCtaClicked,
  trackQuizScreenView,
  trackQuizExitIntentShown,
  trackQuizExitIntentConverted,
  trackQuizExitIntentDismissed,
} from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LS_ANSWERS = 'mundalo_quiz_answers';
const LS_LEAD = 'mundalo_quiz_lead';

// iter 187.n - Module-level dedupe guard for quiz_screen_view. A
// component-level useRef survives normal re-renders but React 18
// StrictMode in dev mounts, unmounts, and re-mounts the component
// which resets the ref. Module scope survives that cycle so each
// (session, screen) pair fires exactly once, even under StrictMode.
// In production StrictMode is stripped, so this is belt-and-suspenders.
const _firedScreenKeys = new Set();
const _firedSessionId = String(Date.now()) + '-' + Math.random().toString(36).slice(2, 8);

// ---------- Question schema (single source of truth) ----------
const QUESTIONS = {
  q1: {
    key: 'tuesday_morning',
    title: 'What does Tuesday morning look like in the life you actually want?',
    options: [
      'A slow coffee on a terrace with nowhere to be',
      'My kids playing outside in a neighborhood that feels safe',
      'Working two hours and calling it a day',
      'Waking up somewhere that finally feels like mine',
      "I can't picture it yet and that's exactly why I'm here",
    ],
  },
  q2: {
    key: 'how_long',
    title: 'How long have you been having this conversation with yourself?',
    options: [
      "It started after a trip I couldn't stop thinking about",
      'A few months - something shifted recently',
      "Longer than I want to admit",
      'It comes and goes but it never fully leaves',
      "Something happened and now I can't unsee it",
    ],
  },
  q3: {
    key: 'voice_slowing_down',
    title: "What's the voice that slows you down?",
    options: [
      'Healthcare - what happens if something goes wrong',
      "I don't know a single person there",
      "My family thinks I've lost it",
      "I wouldn't even know where to start",
      'Honestly nothing is stopping me anymore',
    ],
  },
  q4: {
    key: 'who_with',
    title: "Who's making this move with you?",
    options: [
      'Just me - terrifying and exactly the point',
      "Me and my partner - we've been talking about this",
      'My whole family - kids included',
      "Someone who needs convincing - I'm working on it",
      'Still figuring that out',
    ],
  },
  q5: {
    key: 'city',
    title: 'Which city keeps stopping you mid-scroll?',
    options: [
      { label: 'Mexico City', image: 'https://images.unsplash.com/photo-1518105779142-d975f22f1b0a?w=80&q=70' },
      { label: 'Merida', image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=80&q=70' },
      { label: 'San Miguel de Allende', image: 'https://images.unsplash.com/photo-1512813195386-6cf811ad3542?w=80&q=70' },
      { label: 'Puerto Vallarta', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=80&q=70' },
      { label: 'Oaxaca', image: 'https://images.unsplash.com/photo-1518638150340-f706e86654de?w=80&q=70' },
      { label: "I haven't found it yet - that's part of why I need help", image: null },
    ],
  },
  q6: {
    key: 'fear_if_no_go',
    title: "What are you most afraid will happen if you don't go?",
    options: [
      "I'll always wonder what that life felt like",
      "I'll get more comfortable and more stuck",
      'My kids will grow up without knowing another world is possible',
      "I'll keep waiting for the right time until there isn't one",
      "Nothing - and honestly that's the scariest answer",
    ],
  },
  q7: {
    key: 'five_years',
    title: "What would you tell yourself five years from now if you didn't go?",
    options: [
      'That I let fear make the decision',
      'That I waited for permission that was never coming',
      'That I chose comfort over the life I actually wanted',
      'That I let the paperwork win',
      "I refuse to find out - that's why I'm here",
    ],
  },
  q8: {
    key: 'have_that_you_dont',
    title: "What does someone who already made this move have that you don't yet?",
    options: [
      'The plan - they knew exactly what steps to take',
      'The courage - they just decided',
      'The information - they found the right guide',
      "Nothing - I just haven't started",
      'Permission - someone told them they were ready',
    ],
  },
};

const TOTAL_SCREENS = 14;

// ---------- Reusable shells ----------
const QuizShell = ({ children, progress, onTapAdvance }) => (
  <div
    onClick={onTapAdvance}
    data-testid="quiz-shell"
    className={`fixed inset-0 bg-[#0D1F2D] text-[#F4ECD8] overflow-y-auto ${
      onTapAdvance ? 'cursor-pointer' : ''
    }`}
  >
    {/* Progress bar - the only progress indicator on the entire flow. */}
    <div
      data-testid="quiz-progress-bar"
      className="fixed top-0 left-0 right-0 h-1 bg-white/5 z-50"
    >
      <div
        className="h-full bg-[#E8A830] transition-[width] duration-500 ease-out"
        style={{ width: `${Math.min(100, Math.max(0, progress * 100)).toFixed(1)}%` }}
      />
    </div>
    {children}
  </div>
);

const OptionButton = ({ children, onSelect, image, testid }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onSelect(); }}
    data-testid={testid}
    className="w-full text-left bg-white/[0.04] border border-white/15 hover:border-[#E8A830] hover:bg-[#E8A830]/10 active:bg-[#E8A830]/20 transition-colors rounded-2xl px-5 py-4 md:px-6 md:py-5 flex items-center gap-4"
  >
    {image && (
      <img
        src={image}
        alt=""
        className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-white/10"
        loading="lazy"
      />
    )}
    <span className="text-[#F4ECD8] text-base md:text-lg leading-snug">{children}</span>
  </button>
);

const FadeIn = ({ keyProp, children }) => (
  <div
    key={keyProp}
    className="animate-[fadeIn_0.5s_ease-out] min-h-screen flex flex-col"
  >
    {children}
  </div>
);

// ---------- Screen 14 - 5-dimension personalization ----------
// iter 144r - Replaces the older single-headline buildResult() with a
// structured Mirror / Bridge content engine. We pull five orthogonal
// signals from the answer set and map each to a hand-written copy
// fragment so the result page reads like it was written for this one
// reader, not "personalized" via Mad Libs.
//
//   city       (q5) - destination signal
//   family     (q4) - household composition
//   fear       (q3) - top hesitation
//   timeline   (q2) - how long they've been carrying this
//   readiness  (q7+q8 combined) - narrative arc the reader wants to hear
//
// Anything that doesn't match a known bucket falls to a neutral default
// so the page never renders an empty section.
const KNOWN_CITIES = ['Mexico City', 'Merida', 'San Miguel de Allende',
                      'Puerto Vallarta', 'Oaxaca'];

function _matchCity(q5 = '') {
  return KNOWN_CITIES.find((c) => q5.startsWith(c)) || null;
}

function _familyBucket(q4 = '') {
  if (q4.includes('whole family') || q4.includes('kids included')) return 'family';
  if (q4.includes('partner')) return 'partner';
  if (q4.includes('Just me')) return 'solo';
  if (q4.includes('convincing')) return 'convincing';
  return 'unsure';
}

function _fearKey(q3 = '') {
  if (q3.includes('Healthcare')) return 'healthcare';
  if (q3.includes("don't know a single person")) return 'lonely';
  if (q3.includes("lost it")) return 'family_skeptic';
  if (q3.includes("wouldn't even know where to start")) return 'no_plan';
  if (q3.includes('Honestly nothing')) return 'fearless';
  return 'default';
}

function _timelineKey(q2 = '') {
  if (q2.includes("after a trip")) return 'after_trip';
  if (q2.includes('A few months')) return 'few_months';
  if (q2.includes("Longer than I want to admit")) return 'long';
  if (q2.includes('comes and goes')) return 'comes_goes';
  if (q2.includes('Something happened')) return 'event';
  return 'default';
}

const MIRROR_FEAR = {
  healthcare:     "Your worry isn't crazy - it's the #1 reason Americans hesitate. And the #1 thing they laugh about six months in.",
  lonely:         "The loneliness fear is real. It also disappears faster than you expect - usually inside the first 30 days.",
  family_skeptic: "Every person who made this move had someone close to them who didn't get it. Then they visited.",
  no_plan:        "Not knowing where to start isn't a personality flaw. It's the entire problem we built Mundalo to solve.",
  fearless:       "You're not the kind of person who needs convincing. You're the kind who needs the plan.",
  default:        "Whatever's slowing you down - you're not the first to feel it, and you won't be the last to move through it.",
};

const MIRROR_TIMELINE = {
  after_trip:   "Something on that trip didn't let you go.",
  few_months:   "Something shifted recently - and you noticed.",
  long:         "You've been carrying this longer than you've told most people.",
  comes_goes:   "It comes back. It always comes back. That's the signal.",
  event:        "Something happened. And now you can't unsee it.",
  default:      "You didn't end up on this page by accident.",
};

// city → {solo, family} bridge copy. `solo` covers solo + partner + unsure.
const BRIDGE_BY_CITY = {
  'Mexico City': {
    solo:   "CDMX rewards the curious. You'll find your neighborhood in week one.",
    family: "CDMX has the deepest international-school bench in Latin America. Polanco, Roma, Condesa - your family will find their pocket.",
  },
  'Merida': {
    solo:   "Mérida is the quietest entry point into Mexico. Safe, slow, and full of Americans who already made the leap.",
    family: "Mérida is the #1 American-family city in Mexico. The schools are excellent, the streets are safe, and most families are fully settled inside 90 days.",
  },
  'San Miguel de Allende': {
    solo:   "SMA is built for people who want their lives to feel beautiful. The expat infrastructure is the strongest in the country.",
    family: "SMA has been raising American kids for 50+ years. The bilingual school network is mature and the community is already yours.",
  },
  'Puerto Vallarta': {
    solo:   "PV is the easiest soft landing - English everywhere, beach everywhere, and a visa path most Americans qualify for.",
    family: "PV gives families the beach-town childhood most American kids never get to have. Schools are solid and the LGBTQ+ family community is huge.",
  },
  'Oaxaca': {
    solo:   "Oaxaca is the deep cut. You picked the city Americans go to when they're done with the easy version.",
    family: "Oaxaca raises kids who grow up bilingual, food-literate, and rooted in something. Schools are smaller but the community is unmatched.",
  },
};

const BRIDGE_NO_CITY = {
  solo:   "You don't have a city yet - and that's not a problem, it's a question. The Blueprint answers it.",
  family: "Your family's city is in the Blueprint. We ask the 20 questions that surface the right one for your kids' ages, your budget, and your timeline.",
};

function buildResultV2(answers) {
  const city = _matchCity(answers.q5);
  const fam = _familyBucket(answers.q4);
  const isFamily = fam === 'family';
  const fearKey = _fearKey(answers.q3);
  const tlKey = _timelineKey(answers.q2);

  const mirrorOpener = MIRROR_TIMELINE[tlKey] || MIRROR_TIMELINE.default;
  const mirrorBody = MIRROR_FEAR[fearKey] || MIRROR_FEAR.default;

  const bridgeRow = city ? BRIDGE_BY_CITY[city] : BRIDGE_NO_CITY;
  const bridge = isFamily ? bridgeRow.family : bridgeRow.solo;

  // Top-of-page headline keeps the original logic so this still feels
  // like a continuation of the older flow when copy is being A/B'd.
  let headline;
  if (city && isFamily) {
    headline = `${city} is the right call for your family.`;
  } else if (city) {
    headline = `${city} is closer than you think.`;
  } else if (isFamily) {
    headline = "Your family is the reason - and the reason it works.";
  } else if (fearKey === 'fearless') {
    headline = "You already know. Now let's build the plan.";
  } else {
    headline = "You're more ready than you think.";
  }

  return {
    headline,
    mirrorOpener,
    mirrorBody,
    bridge,
    knownCity: city,
    isFamily,
  };
}

// ---------- Main component ----------
export default function StartQuiz() {
  const navigate = useNavigate();
  // iter 150 - Honor ?return= so gated entry points (e.g. View Profile
  // on /find-a-professional) forward the visitor to the requested URL
  // the moment authentication completes. Persisted to sessionStorage so
  // the param survives the multi-screen quiz flow even if React Router
  // strips the query string mid-funnel.
  const [searchParams] = useSearchParams();
  const { user: authedUser, loading: authLoading } = useAuth();
  useEffect(() => {
    const ret = searchParams.get('return');
    if (ret) {
      try { window.sessionStorage.setItem('mundalo_post_auth_return', ret); }
      catch { /* storage blocked */ }
    }
  }, [searchParams]);
  useEffect(() => {
    if (authLoading) return;
    if (!authedUser) return;
    // Already signed in - skip the lead-capture quiz entirely.
    let ret = '';
    try { ret = window.sessionStorage.getItem('mundalo_post_auth_return') || ''; }
    catch { ret = ''; }
    if (!ret) ret = searchParams.get('return') || '/portal/overview';
    try { window.sessionStorage.removeItem('mundalo_post_auth_return'); }
    catch { /* noop */ }
    // Only allow internal redirects to avoid open-redirect risk.
    if (ret.startsWith('/')) navigate(ret, { replace: true });
  }, [authLoading, authedUser, navigate, searchParams]);
  const [screen, setScreen] = useState(1);
  const [answers, setAnswers] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_ANSWERS) || '{}'); }
    catch { return {}; }
  });
  const [emailForm, setEmailForm] = useState({ first_name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  // Loading-screen progress
  const [loadingPct, setLoadingPct] = useState(0);
  const [loadingStep, setLoadingStep] = useState(0);

  // iter 144q - Persist answers to localStorage on every change so
  // an accidental tab close mid-flow doesn't lose the lead's signal.
  useEffect(() => {
    try { localStorage.setItem(LS_ANSWERS, JSON.stringify(answers)); }
    catch { /* storage blocked */ }
  }, [answers]);

  // Strip the global app header/footer for the entire /start lifetime.
  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, []);

  // iter 144u - Drop-off tracking. Fire `quiz_screen_view` on every
  // screen advance, including the initial mount (screen=1), buffer
  // screens (5, 10), the email gate (6), the loading screen (13), and
  // the result (14). Lets us read funnel drop-off straight from GA4.
  //
  // iter 187.n - Gate the fire on a module-level Set so the effect
  // never emits the same screen number twice per browser session,
  // even when React 18 StrictMode double-invokes the effect in dev.
  // Cast `screen` to Number so gtag transmits it as `epn.screen`
  // (numeric) instead of `ep.screen` (text), enabling numeric range
  // filters in GA4 Explorations.
  useEffect(() => {
    const key = `${_firedSessionId}:${screen}`;
    if (_firedScreenKeys.has(key)) return;
    _firedScreenKeys.add(key);
    trackQuizScreenView({ screen: Number(screen) });
  }, [screen]);

  const progress = (screen - 1) / (TOTAL_SCREENS - 1);

  const recordAnswer = (qkey, value) => {
    setAnswers((prev) => ({ ...prev, [qkey]: value }));
    // Auto-advance with a 300ms beat so the user sees their pick highlight.
    setTimeout(() => setScreen((s) => s + 1), 300);
  };

  // Email-gate submission - POST to backend, then continue.
  const submitEmail = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    const email = emailForm.email.trim().toLowerCase();
    const first_name = emailForm.first_name.trim();
    if (!first_name) { setEmailError('Please add your first name.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setEmailError('That email looks off - try again.'); return; }
    setEmailError('');
    setSubmitting(true);
    try {
      let utm = {};
      try { utm = JSON.parse(localStorage.getItem('mundalo_utm') || '{}'); } catch { /* */ }
      const body = {
        email,
        first_name,
        answers,
        promo_code: getStoredPromo() || undefined,
        utm_source:   utm.utm_source,
        utm_medium:   utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        utm_content:  utm.utm_content,
      };
      const r = await fetch(`${API_URL}/api/quiz/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const data = await r.json();
        try { localStorage.setItem(LS_LEAD, JSON.stringify({ ...data, email, first_name })); }
        catch { /* */ }
        trackQuizEmailCaptured({ source: 'start_quiz' });
        setScreen((s) => s + 1);
      } else {
        setEmailError('Something went wrong. Try again in a moment.');
      }
    } catch {
      setEmailError("We couldn't save that. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Buffer-screen auto-advance + tap-anywhere helper
  const bufferRef = useRef(null);
  useEffect(() => {
    if (screen === 5 || screen === 10) {
      const t = setTimeout(() => setScreen((s) => s + 1), 5000);
      bufferRef.current = t;
      return () => clearTimeout(t);
    }
    if (screen === 13) {
      // 8-second loading bar
      const start = Date.now();
      const tick = setInterval(() => {
        const elapsed = (Date.now() - start) / 1000;
        setLoadingPct(Math.min(1, elapsed / 8));
        setLoadingStep(elapsed < 2 ? 0 : elapsed < 4 ? 1 : elapsed < 6 ? 2 : 3);
        if (elapsed >= 8) {
          clearInterval(tick);
          // Fire completed event + advance.
          const r = buildResultV2(answers);
          trackQuizCompleted({ city: r.knownCity, situation: answers.q4 || null });
          setScreen(14);
        }
      }, 120);
      return () => clearInterval(tick);
    }
  }, [screen, answers]);

  const tapAdvanceBuffer = () => {
    if (screen === 5 || screen === 10) {
      clearTimeout(bufferRef.current);
      setScreen((s) => s + 1);
    }
  };

  // CTA routing - pre-applies a promo + tier.
  //   plan ∈ {'blueprint_only', 'essentials', 'community'}
  //   promo defaults to LAUNCH25, but the exit-intent overlay passes
  //   QUIZ50 to swap in the $100-off Blueprint coupon.
  const goCheckout = (plan, promo = 'LAUNCH25') => {
    trackQuizCtaClicked({ plan });
    if (plan === 'community') {
      navigate(`/relocation/community?promo=${promo}`);
      return;
    }
    const tier = plan === 'blueprint_only' ? 'blueprint_only' : 'essentials';
    navigate(`/relocation/blueprint?promo=${promo}&tier=${tier}`);
  };

  const result = useMemo(() => buildResultV2(answers), [answers]);

  // ---------- Exit-intent overlay (iter 144r) ----------
  // Trigger logic on Screen 14 only:
  //   • Desktop  → `mouseleave` from the top edge of the viewport.
  //   • Mobile   → 90-second idle on this screen (no scroll, no touch).
  // The overlay is shown at most once per quiz session. Once dismissed
  // or converted it does not re-trigger.
  const [exitOpen, setExitOpen] = useState(false);
  const [exitFired, setExitFired] = useState(false);
  const exitTriggerRef = useRef(null); // remembers which trigger opened it

  useEffect(() => {
    if (screen !== 14 || exitFired) return undefined;

    const openOverlay = (trigger) => {
      if (exitFired) return;
      setExitFired(true);
      exitTriggerRef.current = trigger;
      setExitOpen(true);
      trackQuizExitIntentShown({ trigger });
    };

    // Desktop mouseleave-top detector.
    const onMouseOut = (e) => {
      // Browsers fire mouseout with relatedTarget null when the cursor
      // crosses the viewport edge. clientY <= 0 isolates the top-edge
      // exit specifically (which is what desktop users do when they
      // reach for a tab close or the URL bar).
      if (e.clientY <= 0 && !e.relatedTarget) openOverlay('mouseleave');
    };
    document.addEventListener('mouseout', onMouseOut);

    // Mobile 90-second idle detector. Any scroll or touch resets it.
    let idleTimer = setTimeout(() => openOverlay('idle_90s'), 90_000);
    const resetIdle = () => {
      clearTimeout(idleTimer);
      if (!exitFired) idleTimer = setTimeout(() => openOverlay('idle_90s'), 90_000);
    };
    window.addEventListener('scroll', resetIdle, { passive: true });
    window.addEventListener('touchstart', resetIdle, { passive: true });

    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
      clearTimeout(idleTimer);
    };
  }, [screen, exitFired]);

  const handleExitConvert = () => {
    trackQuizExitIntentConverted({ trigger: exitTriggerRef.current });
    setExitOpen(false);
    // Route to Blueprint with QUIZ50 pre-applied. App.js's
    // capturePromoFromUrl() will overwrite any existing stored promo.
    goCheckout('blueprint_only', 'QUIZ50');
  };

  const handleExitDismiss = () => {
    trackQuizExitIntentDismissed({ trigger: exitTriggerRef.current });
    setExitOpen(false);
  };

  // ---------- Renderers ----------
  return (
    <QuizShell
      progress={progress}
      onTapAdvance={(screen === 5 || screen === 10) ? tapAdvanceBuffer : undefined}
    >
      {/* keyframes - kept inline so this page truly is self-contained */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ===== Screen 1 - Hero ===== */}
      {screen === 1 && (
        <FadeIn keyProp="s1">
          <div className="flex-1 flex flex-col items-center justify-center px-5 py-8 md:py-10 max-w-2xl mx-auto w-full">
            <div className="w-full relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 mb-6 md:mb-8 aspect-video">
              <img
                src="https://img.youtube.com/vi/xOOrKVply5k/maxresdefault.jpg"
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#0D1F2D]/40" />
            </div>
            {/* iter 144v - Cold-traffic context line. Tells Instagram
                visitors in one glance who this funnel is for before they
                read anything else. */}
            <p
              data-testid="quiz-context-line"
              className="uppercase tracking-[0.22em] text-[11px] md:text-xs text-[#E8A830] mb-3 md:mb-4 text-center"
              style={{ fontFamily: "'Space Mono', 'JetBrains Mono', monospace" }}
            >
              For Americans Moving to Mexico
            </p>
            <h1 className="font-display italic text-[1.875rem] md:text-5xl leading-tight text-center text-[#F4ECD8] mb-5 md:mb-6">
              You didn&rsquo;t end up here by accident.
            </h1>
            <p className="font-mono uppercase tracking-[0.22em] text-xs md:text-sm text-[#E8A830] mb-7 md:mb-10 text-center">
              2 minutes · your personalized mexico plan
            </p>
            <button
              data-testid="quiz-cta-start"
              onClick={() => { trackQuizStarted(); setScreen(2); }}
              className="w-full bg-[#E8A830] hover:bg-[#f0b748] text-[#1a1a1a] font-semibold py-4 rounded-full text-base md:text-lg transition-colors"
            >
              I&rsquo;m ready
            </button>
          </div>
        </FadeIn>
      )}

      {/* ===== Screens 2-4 - Q1-Q3 ===== */}
      {[2, 3, 4].includes(screen) && (() => {
        const map = { 2: 'q1', 3: 'q2', 4: 'q3' };
        const qkey = map[screen];
        const q = QUESTIONS[qkey];
        return (
          <FadeIn keyProp={qkey}>
            <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-2xl mx-auto w-full">
              <h2 className="font-display text-[1.75rem] md:text-4xl leading-snug text-center text-[#F4ECD8] mb-10">
                {q.title}
              </h2>
              <div className="space-y-3 md:space-y-4">
                {q.options.map((opt, i) => (
                  <OptionButton
                    key={i}
                    testid={`quiz-${qkey}-option-${i}`}
                    onSelect={() => recordAnswer(qkey, opt)}
                  >
                    {opt}
                  </OptionButton>
                ))}
              </div>
            </div>
          </FadeIn>
        );
      })()}

      {/* ===== Screen 5 - Buffer 1 ===== */}
      {screen === 5 && (
        <FadeIn keyProp="s5">
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-2xl mx-auto w-full text-center">
            <p className="font-display italic text-2xl md:text-4xl leading-snug text-[#F4ECD8] mb-8">
              Every single person who makes this move says the same thing when they get there.
            </p>
            <p className="font-display italic text-4xl md:text-6xl leading-tight text-[#E8A830] mb-10">
              &ldquo;I waited way too long.&rdquo;
            </p>
            <p className="font-mono text-xs md:text-sm text-[#F4ECD8]/70 tracking-wide max-w-md">
              We built Mundalo so you don&rsquo;t have to say that.
            </p>
          </div>
        </FadeIn>
      )}

      {/* ===== Screen 6 - Email gate ===== */}
      {screen === 6 && (
        <FadeIn keyProp="s6">
          <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-md mx-auto w-full">
            <p className="font-display italic text-2xl md:text-3xl leading-snug text-center text-[#F4ECD8] mb-3">
              Most people who get this far have been thinking about this for over a year.
            </p>
            <p className="font-mono text-xs md:text-sm text-[#F4ECD8]/70 tracking-wide text-center mb-10">
              We built something for exactly where you are.
            </p>
            <form onSubmit={submitEmail} className="space-y-4" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                placeholder="First name"
                data-testid="quiz-input-first-name"
                value={emailForm.first_name}
                onChange={(e) => setEmailForm((f) => ({ ...f, first_name: e.target.value }))}
                className="w-full bg-transparent border border-[#F4ECD8]/30 focus:border-[#E8A830] text-[#F4ECD8] placeholder:text-[#F4ECD8]/40 rounded-lg px-4 py-3 outline-none transition-colors"
              />
              <input
                type="email"
                placeholder="Email address"
                data-testid="quiz-input-email"
                value={emailForm.email}
                onChange={(e) => setEmailForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full bg-transparent border border-[#F4ECD8]/30 focus:border-[#E8A830] text-[#F4ECD8] placeholder:text-[#F4ECD8]/40 rounded-lg px-4 py-3 outline-none transition-colors"
              />
              {emailError && (
                <p data-testid="quiz-email-error" className="text-sm text-[#E8A830]">{emailError}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="quiz-email-submit"
                className="w-full bg-[#E8A830] hover:bg-[#f0b748] disabled:opacity-60 text-[#1a1a1a] font-semibold py-4 rounded-full transition-colors"
              >
                {submitting ? 'One moment…' : 'Show me my plan'}
              </button>
            </form>
          </div>
        </FadeIn>
      )}

      {/* ===== Screens 7-9 + 11-12 - Q4-Q8 ===== */}
      {[7, 8, 9, 11, 12].includes(screen) && (() => {
        const map = { 7: 'q4', 8: 'q5', 9: 'q6', 11: 'q7', 12: 'q8' };
        const qkey = map[screen];
        const q = QUESTIONS[qkey];
        return (
          <FadeIn keyProp={qkey}>
            <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-2xl mx-auto w-full">
              <h2 className="font-display text-[1.75rem] md:text-4xl leading-snug text-center text-[#F4ECD8] mb-10">
                {q.title}
              </h2>
              <div className="space-y-3 md:space-y-4">
                {q.options.map((opt, i) => {
                  const label = typeof opt === 'string' ? opt : opt.label;
                  const image = typeof opt === 'string' ? null : opt.image;
                  return (
                    <OptionButton
                      key={i}
                      testid={`quiz-${qkey}-option-${i}`}
                      image={image}
                      onSelect={() => recordAnswer(qkey, label)}
                    >
                      {label}
                    </OptionButton>
                  );
                })}
              </div>
            </div>
          </FadeIn>
        );
      })()}

      {/* ===== Screen 10 - Buffer 2 ===== */}
      {screen === 10 && (
        <FadeIn keyProp="s10">
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-2xl mx-auto w-full text-center">
            <p className="font-display italic text-xl md:text-2xl text-[#F4ECD8] mb-8">
              Hundreds of thousands of Americans have made this move.
            </p>
            <p className="font-display italic text-3xl md:text-5xl leading-tight text-[#F4ECD8] mb-8">
              Most of them said the same thing when they arrived:
            </p>
            <p className="font-display italic text-4xl md:text-6xl leading-tight text-[#E8A830] mb-10">
              &ldquo;Why did I wait so long.&rdquo;
            </p>
            <p className="font-mono text-xs md:text-sm text-[#F4ECD8]/70 tracking-wide">
              You&rsquo;re closer than you think. Keep going.
            </p>
          </div>
        </FadeIn>
      )}

      {/* ===== Screen 13 - Loading ===== */}
      {screen === 13 && (
        <FadeIn keyProp="s13">
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-md mx-auto w-full text-center">
            <p className="font-display italic text-2xl md:text-4xl leading-snug text-[#F4ECD8] mb-10">
              Building your personalized relocation plan…
            </p>
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-12">
              <div
                className="h-full bg-[#E8A830] transition-[width] duration-100 ease-out"
                style={{ width: `${(loadingPct * 100).toFixed(1)}%` }}
                data-testid="quiz-loading-bar"
              />
            </div>
            <div className="space-y-3 font-mono text-sm md:text-base text-[#F4ECD8]/80 min-h-[120px]">
              {loadingStep >= 1 && <p className="animate-[fadeIn_0.5s_ease-out]">Analyzing your timeline and situation</p>}
              {loadingStep >= 2 && <p className="animate-[fadeIn_0.5s_ease-out]">Matching you to the right visa path</p>}
              {loadingStep >= 3 && <p className="animate-[fadeIn_0.5s_ease-out]">Identifying your city fit</p>}
            </div>
          </div>
        </FadeIn>
      )}

      {/* ===== Screen 14 - Result + purchase (iter 144r) =====
          Five fixed sections, always rendered in order:
            (1) MIRROR     - names back fear + timeline
            (2) BRIDGE     - city × family combination
            (3) PRIMARY    - Blueprint Only (LAUNCH25 → $149)
            (4) ALTERNATIVE - Essentials   (LAUNCH25 → $261)
            (5) COMMUNITY  - soft, $9.99/mo
          The exit-intent overlay (QUIZ50 → $99) is rendered at the
          bottom of the QuizShell so it stacks above this content. */}
      {screen === 14 && (
        <FadeIn keyProp="s14">
          <div className="flex-1 flex flex-col items-center px-5 py-12 md:py-16 max-w-3xl mx-auto w-full">
            <img
              src={BRAND_LOGO.white}
              alt="Mundalo"
              className="h-7 md:h-8 w-[140px] md:w-[160px] object-contain object-left mb-10"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))' }}
            />

            {/* Top headline - picked from the city/family/fearless rules */}
            <h1
              data-testid="quiz-result-headline"
              className="font-display text-3xl md:text-5xl leading-tight text-[#F4ECD8] text-center mb-10"
            >
              {result.headline}
            </h1>

            {/* (1) MIRROR - timeline opener + fear acknowledgement */}
            <section
              data-testid="quiz-section-mirror"
              className="w-full mb-10 md:mb-12"
            >
              <p className="font-display italic text-xl md:text-2xl leading-snug text-[#F4ECD8] text-center mb-3">
                {result.mirrorOpener}
              </p>
              <p className="text-base md:text-lg text-[#F4ECD8]/85 text-center max-w-xl mx-auto leading-relaxed">
                {result.mirrorBody}
              </p>
            </section>

            {/* (2) BRIDGE - city × family combination */}
            <section
              data-testid="quiz-section-bridge"
              className="w-full mb-12 md:mb-14"
            >
              <p className="font-mono uppercase tracking-[0.22em] text-[10px] md:text-xs text-[#E8A830] text-center mb-4">
                {result.knownCity ? 'Your city' : 'Your fit'}
              </p>
              <p className="text-base md:text-lg text-[#F4ECD8] text-center max-w-xl mx-auto leading-relaxed">
                {result.bridge}
              </p>
            </section>

            <p className="font-display italic text-2xl md:text-3xl text-[#F4ECD8] text-center mb-8">
              Your Blueprint is ready. We built it for exactly where you are.
            </p>

            {/* (3) PRIMARY OFFER - Blueprint Only */}
            <section
              data-testid="quiz-section-primary"
              className="w-full"
            >
              <div
                data-testid="quiz-card-blueprint"
                className="w-full bg-white/[0.04] border-2 border-[#E8A830] rounded-2xl p-6 md:p-8 mb-4"
              >
                <p className="font-mono uppercase tracking-[0.18em] text-xs text-[#E8A830] mb-2">
                  {PRICING_CONTENT.blueprint_only.name}
                </p>
                <p
                  className="font-display italic text-[#E8A830] text-lg md:text-xl mb-3"
                  data-testid="quiz-card-blueprint-tagline"
                >
                  {PRICING_CONTENT.blueprint_only.tagline}
                </p>
                <p className="text-[#F4ECD8] mb-4">
                  <span className="line-through opacity-50 mr-2 text-lg">$199</span>
                  <span className="text-3xl md:text-4xl font-semibold">$149</span>
                  <span className="text-sm ml-2 opacity-70">today with LAUNCH25</span>
                </p>
                <p
                  className="text-sm md:text-base text-[#F4ECD8]/85 leading-relaxed mb-4"
                  data-testid="quiz-card-blueprint-description"
                >
                  {PRICING_CONTENT.blueprint_only.description}
                </p>
                <ul className="space-y-1.5 mb-5" data-testid="quiz-card-blueprint-bullets">
                  {PRICING_CONTENT.blueprint_only.bullets.map((b) => (
                    <li key={b} className="text-sm text-[#F4ECD8]/85 flex items-start gap-2">
                      <span className="text-[#E8A830] mt-0.5">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => goCheckout('blueprint_only')}
                  data-testid="quiz-cta-blueprint"
                  className="w-full bg-[#E8A830] hover:bg-[#f0b748] text-[#1a1a1a] font-semibold py-3.5 rounded-full transition-colors"
                >
                  Get My Blueprint →
                </button>
              </div>
            </section>

            {/* (4) SOFT ALTERNATIVE - Essentials */}
            <section
              data-testid="quiz-section-alternative"
              className="w-full"
            >
              <div
                data-testid="quiz-card-essentials"
                className="w-full bg-white/[0.03] border border-white/15 rounded-2xl p-6 md:p-8 mb-4"
              >
                <p className="font-mono uppercase tracking-[0.18em] text-xs text-[#F4ECD8]/75 mb-2">
                  {PRICING_CONTENT.essentials.name}
                </p>
                <p
                  className="font-display italic text-[#F4ECD8]/85 text-lg md:text-xl mb-3"
                  data-testid="quiz-card-essentials-tagline"
                >
                  {PRICING_CONTENT.essentials.tagline}
                </p>
                <p className="text-[#F4ECD8] mb-4">
                  <span className="line-through opacity-50 mr-2 text-lg">$349</span>
                  <span className="text-3xl md:text-4xl font-semibold">$261</span>
                  <span className="text-sm ml-2 opacity-70">today with LAUNCH25</span>
                </p>
                <p
                  className="text-sm md:text-base text-[#F4ECD8]/85 leading-relaxed mb-4"
                  data-testid="quiz-card-essentials-description"
                >
                  {PRICING_CONTENT.essentials.description}
                </p>
                <ul className="space-y-1.5 mb-5" data-testid="quiz-card-essentials-bullets">
                  {PRICING_CONTENT.essentials.bullets.map((b) => (
                    <li key={b} className="text-sm text-[#F4ECD8]/85 flex items-start gap-2">
                      <span className="text-[#2CA8A0] mt-0.5">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => goCheckout('essentials')}
                  data-testid="quiz-cta-essentials"
                  className="w-full bg-white/10 hover:bg-white/20 text-[#F4ECD8] border border-white/20 hover:border-[#E8A830] font-semibold py-3.5 rounded-full transition-colors"
                >
                  Get Essentials →
                </button>
              </div>
            </section>

            {/* Community - subdued */}
            <div
              data-testid="quiz-card-community"
              className="w-full bg-transparent border border-white/10 rounded-xl p-5 md:p-6 mb-6"
            >
              <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#F4ECD8]/55 mb-2">Not ready to commit yet?</p>
              <p className="text-[#F4ECD8] text-base md:text-lg mb-3">
                Community - <span className="font-semibold">$9.99/month</span>
              </p>
              <p className="text-sm text-[#F4ECD8]/70 leading-relaxed mb-4">
                You don&rsquo;t have to figure this out alone. Join Americans planning the move and Americans who already made it. Ask anything. Get real answers. Monthly events, Peer Circles, and a community that actually feels like your people.
              </p>
              <button
                onClick={() => goCheckout('community')}
                data-testid="quiz-cta-community"
                className="text-sm font-mono uppercase tracking-[0.16em] text-[#E8A830] hover:text-[#f0b748] transition-colors"
              >
                Join the Community →
              </button>
            </div>

            <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.18em] text-[#F4ECD8]/60 text-center mb-4">
              LAUNCH25 applied automatically · Offer ends this week
            </p>
            <p className="font-display italic text-base md:text-lg text-[#F4ECD8]/85 text-center max-w-xl">
              Something brought you here today. Don&rsquo;t let it be the thing you almost did.
            </p>
          </div>
        </FadeIn>
      )}

      {/* ===== (5) EXIT-INTENT OVERLAY - QUIZ50 → $99 Blueprint ===== */}
      {exitOpen && (
        <div
          data-testid="quiz-exit-intent-overlay"
          onClick={handleExitDismiss}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center px-5 py-8 animate-[fadeIn_0.25s_ease-out]"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0D1F2D] border-2 border-[#E8A830] rounded-2xl p-7 md:p-8 shadow-2xl"
          >
            <p className="font-mono uppercase tracking-[0.22em] text-[10px] md:text-xs text-[#E8A830] mb-3">
              One last thing
            </p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight text-[#F4ECD8] mb-4">
              Wait - before you go.
            </h2>
            <p className="text-sm md:text-base text-[#F4ECD8]/85 leading-relaxed mb-6">
              We know this is a big decision. Take $100 off your Blueprint today - only for people who started the quiz. One-time, expires when you close this tab.
            </p>
            <div className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 mb-6">
              <p className="text-[#F4ECD8]">
                <span className="line-through opacity-50 mr-2">$199</span>
                <span className="text-3xl md:text-4xl font-semibold">$99</span>
                <span className="text-xs ml-2 opacity-70">with QUIZ50</span>
              </p>
            </div>
            <button
              onClick={handleExitConvert}
              data-testid="quiz-exit-intent-cta"
              className="w-full bg-[#E8A830] hover:bg-[#f0b748] text-[#1a1a1a] font-semibold py-3.5 rounded-full transition-colors mb-3"
            >
              Get my Blueprint for $99
            </button>
            <button
              onClick={handleExitDismiss}
              data-testid="quiz-exit-intent-dismiss"
              className="w-full text-center text-sm text-[#F4ECD8]/55 hover:text-[#F4ECD8]/80 font-mono py-1 transition-colors"
            >
              No thanks, I&rsquo;ll figure it out myself.
            </button>
          </div>
        </div>
      )}
    </QuizShell>
  );
}
